import { useEffect, useRef, useState, useCallback } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import Logo from "@/components/Logo";

const PEOPLE = [
  { name: "Brian Bususu", src: "/assets/team-carousel/portrait-02.webp" },
  { name: "Emma Laidlaw", src: "/assets/team-carousel/portrait-05.webp" },
  { name: "Justine Marriott", src: "/assets/team-carousel/portrait-07.webp" },
  { name: "Andrew Norris", src: "/assets/team-carousel/portrait-08.webp" },
  { name: "Clare Samuels", src: "/assets/team-carousel/portrait-09.webp" },
  { name: "Chris Orton", src: "/assets/team-chris-orton.jpg" },
  { name: "Simon Smithies", src: "/assets/team-simon-smithies.jpg" },
  { name: "Maria Gough", src: "/assets/team-maria-gough.jpg" },
  { name: "Hannah Williams", src: "/assets/team-hannah-williams.jpg" },
  { name: "Julia Binns", src: "/assets/team-carousel/portrait-01.webp" },
  { name: "Shireen Farkhoy", src: "/assets/team-carousel/portrait-03.webp" },
  { name: "Keeley Harker", src: "/assets/team-carousel/portrait-04.webp" },
  { name: "Paul Smith", src: "/assets/team-carousel/portrait-10.webp" },
  { name: "Sarah Legg", src: "/assets/team-carousel/portrait-06.webp" },
  { name: "John Yapp", src: "/assets/team-john-yapp.jpg" },
];

export default function TeamCarousel() {
  const [selected, setSelected] = useState(0);
  const [stageWidth, setStageWidth] = useState(1000);
  const stageRef = useRef(null);

  useEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const update = () => setStageWidth(el.clientWidth);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const go = useCallback((dir) => {
    setSelected((s) => (s + dir + PEOPLE.length) % PEOPLE.length);
  }, []);

  const step = Math.min(190, Math.max(100, stageWidth * 0.24));
  const n = PEOPLE.length;

  return (
    <section data-testid="team-carousel" aria-label="Our team photograph carousel" className="text-center">
      <div className="mb-3 flex justify-center">
        <Logo full={false} className="!h-7 sm:!h-8 opacity-80" />
      </div>
      <h3 className="mb-8 text-xl sm:text-2xl font-bold tracking-tight text-navy">Meet our extended team</h3>

      <div
        ref={stageRef}
        data-testid="team-carousel-stage"
        className="relative mx-auto h-[330px] sm:h-[400px] lg:h-[470px] overflow-hidden"
        style={{ perspective: "1100px" }}
        onKeyDown={(e) => {
          if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
            e.preventDefault();
            go(e.key === "ArrowRight" ? 1 : -1);
          }
        }}
      >
        {PEOPLE.map((person, i) => {
          let d = i - selected;
          if (d > n / 2) d -= n;
          if (d < -n / 2) d += n;
          const a = Math.abs(d);
          const visible = a <= 3;
          const x = d * step * (a > 1 ? 0.88 : 1);
          const rot = d === 0 ? 0 : d > 0 ? -48 : 48;
          const scale = a === 0 ? 1 : a === 1 ? 0.83 : a === 2 ? 0.67 : 0.52;
          return (
            <button
              key={person.name}
              type="button"
              aria-label={`Show ${person.name}`}
              aria-hidden={!visible}
              tabIndex={visible ? 0 : -1}
              data-testid={`team-carousel-photo-${person.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              data-active={a === 0 ? "true" : "false"}
              onClick={() => setSelected(i)}
              className="absolute left-1/2 top-[46%] w-[165px] h-[235px] sm:w-[215px] sm:h-[320px] lg:w-[265px] lg:h-[365px] rounded-2xl overflow-hidden cursor-pointer bg-slate-200 border-0 p-0 focus-visible:outline-4 focus-visible:outline-teal focus-visible:outline-offset-4"
              style={{
                transform: `translate(-50%,-50%) translateX(${x}px) translateZ(${-a * 100}px) rotateY(${rot}deg) scale(${scale})`,
                opacity: visible ? (a === 3 ? 0.35 : 1) : 0,
                filter: a > 0 ? "brightness(.84)" : "none",
                zIndex: 20 - a,
                pointerEvents: visible ? "auto" : "none",
                transformOrigin: "center",
                backfaceVisibility: "hidden",
                transition: "transform .65s cubic-bezier(.2,.8,.2,1), opacity .65s, filter .65s",
                boxShadow: a === 0 ? "0 18px 38px #0004" : "0 15px 30px #0002",
              }}
            >
              <img src={person.src} alt={person.name} loading={i < 3 ? "eager" : "lazy"} className="h-full w-full object-cover" />
            </button>
          );
        })}
      </div>

      <p data-testid="team-carousel-caption" aria-live="polite" className="min-h-[38px] font-display text-xl sm:text-2xl font-semibold text-teal">
        {PEOPLE[selected].name}
      </p>

      <div className="mt-3 flex items-center justify-center gap-5">
        <button
          type="button"
          aria-label="Previous team member"
          data-testid="team-carousel-prev"
          onClick={() => go(-1)}
          className="flex h-12 w-12 items-center justify-center rounded-full border border-slate-300 bg-white text-teal transition-colors hover:bg-teal-light"
        >
          <ChevronLeft size={22} />
        </button>
        <span data-testid="team-carousel-counter" className="min-w-[65px] font-mono text-sm text-slate-500">
          {selected + 1} / {n}
        </span>
        <button
          type="button"
          aria-label="Next team member"
          data-testid="team-carousel-next"
          onClick={() => go(1)}
          className="flex h-12 w-12 items-center justify-center rounded-full border border-slate-300 bg-white text-teal transition-colors hover:bg-teal-light"
        >
          <ChevronRight size={22} />
        </button>
      </div>
      <p className="mt-3 px-4 text-xs text-slate-400">Click a portrait or use the arrows to explore the team</p>
    </section>
  );
}
